import { Files, FileStack, CircleAlert } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useImperativeHandle } from 'react';

import { Form, FormField } from 'src/components/ui/form';
import { ToggleGroupCard, ToggleGroupCardItem } from 'src/components/ui/toggle-group';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/components/ui/card';
import { EscrowPartyTypeEnum, AgreementFormEnum } from 'src/graphql/schema';
import { roleOptions } from 'src/pages/SoftwareEscrowOnboarding/model';

import { useSoftwareEscrowNewContext } from '../model';

const formSchema = z.object({
  escrowRole: z.string().min(1, { message: 'Required' }),
  agreementForm: z.string().min(1, { message: 'Required' }),
});

export type IRoleAndAgreementSettingsForm = z.infer<typeof formSchema>;

const RoleAndAgreementSettings = () => {
  const { formRef, setIsValid } = useSoftwareEscrowNewContext();

  const form = useForm<IRoleAndAgreementSettingsForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      escrowRole: '',
      agreementForm: '',
    },
    mode: 'onChange',
  });

  useEffect(() => void form.trigger(), []);

  useImperativeHandle(
    formRef,
    () => ({
      submit: (callback) => form.handleSubmit(callback)(),
    }),
    [],
  );

  const isValid = form.formState.isValid;

  useEffect(() => setIsValid(isValid), [isValid, setIsValid]);

  const role = form.watch('escrowRole');

  useEffect(() => {
    if (role === EscrowPartyTypeEnum.Beneficiary) {
      form.setValue('agreementForm', AgreementFormEnum.Tripartite, {
        shouldValidate: true,
      });
    }
  }, [role]);

  return (
    <Form {...form}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Escrow role
            {form.formState.errors.escrowRole?.message && (
              <span className="text-orange-700 flex items-center gap-1">
                <CircleAlert className="size-3.5" />
                <i className="text-xs font-medium">{form.formState.errors.escrowRole?.message}</i>
              </span>
            )}
          </CardTitle>
          <CardDescription>
            What is your company&apos;s role in the escrow? <br />
            Your selection will customize your view and determine the type of agreement you&apos;ll generate.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FormField
            control={form.control}
            name="escrowRole"
            render={({ field }) => (
              <ToggleGroupCard
                type="single"
                className="flex-col w-full space-y-4"
                onValueChange={field.onChange}
                value={field.value}
              >
                {roleOptions.map((option) => (
                  <ToggleGroupCardItem
                    key={option.value}
                    value={option.value}
                    icon={option.icon}
                    title={option.title}
                    description={option.description}
                  />
                ))}
              </ToggleGroupCard>
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Agreement form
            {form.formState.errors.agreementForm?.message && (
              <span className="text-orange-700 flex items-center gap-1">
                <CircleAlert className="size-3.5" />
                <i className="text-xs font-medium">{form.formState.errors.agreementForm?.message}</i>
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Choose between Bipartite or Tripartite. <br /> This depends on the beneficiary’s involvement in the
            agreement.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FormField
            control={form.control}
            name="agreementForm"
            render={({ field }) => (
              <ToggleGroupCard
                type="single"
                className="flex-col w-full space-y-4"
                onValueChange={field.onChange}
                value={field.value}
              >
                {role !== EscrowPartyTypeEnum.Beneficiary && (
                  <ToggleGroupCardItem
                    value={AgreementFormEnum.Bipartite}
                    icon={Files}
                    title="Bipartite agreement → Beneficiary is NOT involved"
                    description="We'll generate a bipartite agreement between the depositor and the escrow agent. The beneficiary won’t sign, but can still request a release if certain conditions are met."
                  />
                )}
                <ToggleGroupCardItem
                  value={AgreementFormEnum.Tripartite}
                  icon={FileStack}
                  title="Tripartite agreement → Beneficiary IS involved"
                  description="We’ll generate a tripartite agreement signed by the depositor, the beneficiary, and the escrow agent. This will outline each party’s responsibilities in full."
                />
              </ToggleGroupCard>
            )}
          />
        </CardContent>
      </Card>
    </Form>
  );
};
export default RoleAndAgreementSettings;
